<template>
    <div class="infrared-report m60-infrared" :dir="$i18n.locale === 'ar-AR' ? 'rtl' : 'ltr'">
        <div v-if="loading" class="tab-loading">{{ tr("报告加载中，请稍等") }}</div>
        <div v-else-if="loadError" class="tab-fail">{{ loadError }}</div>
        <template v-else>
        <div class="section-head">
            <h2>{{ isFirstMeasure ? tr('热成像结果') : tr('热成像对比') }}</h2>
            <div class="view-toggle">
                <button :class="{ 'is-active': view === 'front' }" type="button" @click="switchView('front')">{{ tr("正面") }}</button>
                <button :class="{ 'is-active': view === 'back' }" type="button" @click="switchView('back')">{{ tr("背面") }}</button>
            </div>
        </div>

        <section class="compare-card" :class="{ 'is-first': isFirstMeasure }">
            <div class="compare-top">
                <span v-if="!isFirstMeasure" class="compare-time">
                    <button type="button" @click.stop="historyOpen = !historyOpen">{{ selectedHistory.label }}</button>
                    <div v-show="historyOpen" class="history-panel">
                        <button
                            v-for="item in historyList"
                            :key="item.scanId"
                            class="history-option"
                            type="button"
                            @click="onSelectHistory(item)"
                        >
                            <span>{{ item.date }}</span>
                            <span>{{ item.time }}</span>
                        </button>
                    </div>
                </span>
                <span v-else class="compare-time"></span>

                <div class="region-picker" :class="{ 'is-open': regionOpen }">
                    <button class="region-current" type="button" @click.stop="regionOpen = !regionOpen">
                        <span>{{ selectedMetric.name }}</span>
                        <span class="chevron">⌄</span>
                    </button>
                    <div v-show="regionOpen" class="region-menu">
                        <button
                            v-for="item in visibleMetrics"
                            :key="item.key"
                            class="region-option"
                            :class="{ 'is-active': item.key === selectedKey }"
                            type="button"
                            @click="selectRegion(item.key)"
                        >{{ item.name }}</button>
                    </div>
                </div>

                <span class="compare-time">{{ currentTimeLabel }}</span>
            </div>

            <div class="visual-compare">
                <div class="thermal-main">
                    <div class="thermal-panes">
                        <div v-if="!isFirstMeasure" class="thermal-pane previous">
                            <div class="heatmap-frame" :data-view="view">
                                <heatmap-viewer
                                    :image-url="compareThermalImg"
                                    :preload-urls="[report.compareFrontImageUrl, report.compareBackImageUrl]"
                                    :view="view"
                                    :rois="report.compareRoiPoints || []"
                                    :selected-key="selectedKey"
                                    @select="selectRegion"
                                />
                            </div>
                        </div>

                        <div class="thermal-pane current">
                            <div class="heatmap-frame" :data-view="view">
                                <heatmap-viewer
                                    :image-url="currentThermalImg"
                                    :preload-urls="[report.frontImageUrl, report.backImageUrl]"
                                    :view="view"
                                    :rois="roiPoints"
                                    :selected-key="selectedKey"
                                    @select="selectRegion"
                                />
                            </div>
                        </div>
                    </div>

                    <div class="focus-panel" @click="openDetail(selectedMetric)">
                        <div class="focus-temp">
                            <b v-if="isFirstMeasure" class="mean">{{ formatTemp(selectedMetric.present) }}</b>
                            <b v-else :class="changeClass(selectedMetric.change)">{{ formatChange(selectedMetric.change) }}</b>
                            <span>{{ isFirstMeasure ? tr('区域均温') : changeLevel(selectedMetric.change) }}</span>
                        </div>
                    </div>
                </div>

                <!-- 色标栏：仅保留颜色渐变，不展示「温度」名称与刻度数值 -->
                <div class="ir-temp-bar" aria-hidden="true">
                    <div class="ir-temp-gradient"></div>
                </div>
            </div>
        </section>

        <div class="module-title">{{ tr("整体评估") }}</div>
        <section class="overall-card">
            <div v-for="(row, idx) in overallRows" :key="idx" class="overall-row">
                <strong>{{ row.label }}</strong>
                <span>{{ row.text }}</span>
            </div>
        </section>

        <div class="module-title">{{ tr("热态数据") }}</div>
        <div class="data-list">
            <button
                v-for="item in metrics"
                :key="item.key"
                class="metric-card"
                :class="{ 'is-active': item.key === selectedKey }"
                type="button"
                @click="selectRegion(item.key); openDetail(item)"
            >
                <div class="metric-compare" :class="{ 'is-first': isFirstMeasure }">
                    <template v-if="isFirstMeasure">
                        <span class="metric-label">
                            <span class="metric-name">{{ item.name }}</span>
                            <small>{{ tr("区域均温") }}</small>
                        </span>
                        <span class="metric-change flat">{{ formatTemp(item.present) }}</span>
                    </template>
                    <template v-else>
                        <div class="metric-side">
                            <span class="metric-label">
                                <span class="metric-name">{{ item.name }}</span>
                                <small>{{ tr("区域均温") }}</small>
                            </span>
                        </div>
                        <div class="metric-center">
                            <span class="metric-change" :class="changeClass(item.change)">{{ formatChange(item.change) }}</span>
                            <span class="metric-level">{{ changeLevel(item.change) }}</span>
                        </div>
                    </template>
                    <span class="metric-arrow">›</span>
                </div>
            </button>
        </div>

        <div class="tip">{{ tr("测量提示：隔衣测量会受衣物材质、厚度和遮挡影响，结果仅供热态分布与趋势参考。") }}</div>
        <div class="tip">{{ tr("复测提示：建议在相同环境、衣着、站位和静止条件下进行阶段性复测。") }}</div>
        <div class="medical-note">{{ tr("声明：本结果仅用于体表热态变化展示和健康管理沟通参考，不作为医学诊断或治疗依据。") }}</div>
        </template>

        <!-- 详情弹层 -->
        <div v-if="sheetOpen" class="sheet-mask" @click="sheetOpen = false"></div>
        <aside v-if="sheetOpen" class="roi-sheet">
            <div class="sheet-head">
                <strong>{{ sheetTitle }}</strong>
                <button class="sheet-close" type="button" @click="sheetOpen = false">×</button>
            </div>
            <div v-if="detailMetric" class="sheet-body">
                <div class="detail-section">
                    <h3 class="detail-section-title">{{ tr('基础数据') }}</h3>
                    <div v-if="!isFirstMeasure" class="detail-compare">
                        <div class="detail-compare-side is-prev"><b>{{ formatTemp(detailMetric.last) }}</b><span>{{ tr('上次区域均温') }}</span></div>
                        <div class="detail-change" :class="changeClass(detailMetric.change)">{{ formatChange(detailMetric.change) }}</div>
                        <div class="detail-compare-side is-curr"><b>{{ formatTemp(detailMetric.present) }}</b><span>{{ tr('本次区域均温') }}</span></div>
                    </div>
                    <div class="detail-grid" :class="{ 'detail-grid-4': isFirstMeasure }">
                        <div v-for="field in detailFields" :key="field.key" class="detail-field"><span>{{ tr(field.label) }}</span><b>{{ formatTemp(detailMetric[field.key], field.diff) }}</b></div>
                    </div>
                </div>
                <div class="detail-section">
                    <h3 class="detail-section-title">{{ tr('左右差异') }}</h3>
                    <div v-if="detailMetric.leftAvgTemp != null && detailMetric.rightAvgTemp != null" class="detail-grid">
                        <div class="detail-field"><span>{{ tr('左侧均温') }}</span><b>{{ formatTemp(detailMetric.leftAvgTemp) }}</b></div>
                        <div class="detail-field"><span>{{ tr('右侧均温') }}</span><b>{{ formatTemp(detailMetric.rightAvgTemp) }}</b></div>
                        <div class="detail-field"><span>{{ tr('左右温差') }}</span><b>{{ formatTemp(detailMetric.leftRightTempDiff, true) }}</b></div>
                    </div>
                    <p v-else class="detail-note">{{ tr('该部位不做左右差异评估。') }}</p>
                </div>
                <div class="detail-section">
                    <h3 class="detail-section-title">{{ tr('部位评估') }}</h3>
                    <div class="detail-eval">
                        <div v-if="!isFirstMeasure" class="detail-eval-row"><strong>{{ tr('变化情况') }}</strong><span>{{ detailMetric.changeText || detailChangeFallback }}</span></div>
                        <div class="detail-eval-row"><strong>{{ tr('分布表现') }}</strong><span>{{ detailMetric.distributionText }}</span></div>
                        <div class="detail-eval-row"><strong>{{ tr('左右差异') }}</strong><span>{{ detailMetric.symmetryText }}</span></div>
                        <div class="detail-eval-row"><strong>{{ tr('服务提示') }}</strong><span>{{ detailMetric.serviceText }}</span></div>
                    </div>
                </div>
            </div>
            <p v-if="detailMetric" class="sheet-foot">{{ tr('区域说明：') }}{{ detailMetric.desc }}</p>
        </aside>

        <!-- 全屏对比 -->
        <section v-if="fullscreenOpen && !isFirstMeasure" class="fullscreen-viewer">
            <div class="viewer-toolbar">
                <strong>{{ tr("热态对比") }}</strong>
                <div>
                    <button type="button" @click="heatSplit = 50">{{ tr("复位") }}</button>
                    <button type="button" @click="fullscreenOpen = false">{{ tr("关闭") }}</button>
                </div>
            </div>
            <div class="viewer-stage">
                <div class="heat-slider" :data-view="view" :style="{ '--split': heatSplit + '%' }">
                    <span class="heat-label left">{{ tr("上次") }}</span>
                    <span class="heat-label right">{{ tr("本次") }}</span>
                    <div class="heat-layer present">
                        <img class="heatmap-img" :src="currentThermalImg" :alt="tr('本次红外热图')" />
                    </div>
                    <div class="heat-layer last">
                        <img class="heatmap-img" :src="compareThermalImg" :alt="tr('上次红外热图')" />
                    </div>
                    <div class="heat-divider"></div>
                    <div class="heat-handle">↔</div>
                    <input v-model.number="heatSplit" class="heat-range" type="range" min="5" max="95" />
                </div>
                <div class="viewer-hint">{{ tr("拖动滑块对比上次与本次热图") }}</div>
            </div>
        </section>
    </div>
</template>

<script>
import { domesticText, infraredView, historyTime } from '@/assets/js/m60sDomesticReport'
import { temperature } from '@/assets/js/m60sInfrared'
import HeatmapViewer from './M60SDomesticHeatmap.vue'

const FALLBACK_IMG = ''

export default {
    components: {
        HeatmapViewer,
    },
    props: {
        reportData: { type: Object, default: () => ({}) },
        massInfo: Object,
        isCreateTime: String,
    },
    data() {
        return {
            loading: true,
            loadError: '',
            report: null,
            historyList: [],
            view: 'front',
            selectedKey: 'frontShoulderNeck',
            selectedHistory: { label: '', scanId: '' },
            historyOpen: false,
            regionOpen: false,
            sheetOpen: false,
            sheetTitle: '',
            sheetBody: '',
            detailMetric: null,
            sheetFoot: '',
            fullscreenOpen: false,
            heatSplit: 50,
            reportLoadToken: 0,
        }
    },
    computed: {
        detailChangeFallback() {
            const metric = this.detailMetric || {};
            const change = metric.change == null ? '--' : this.formatChange(metric.change);
            return this.tr('本次较上次 {change}，{level}。')
                .replace('{change}', change)
                .replace('{level}', metric.changeLevel || this.changeLevel(metric.change));
        },
        detailFields() {
            const fields = [{ key: 'present', label: '区域均温' }, { key: 'high', label: '区域高值' }, { key: 'low', label: '区域低值' }, { key: 'regionTempDiff', label: '区域差值', diff: true }];
            if (!this.isFirstMeasure) fields.shift();
            return fields;
        },
        imperial() { return localStorage.getItem('unit') === 'imperial' },
        isFirstMeasure() {
            return this.report ? this.report.isFirstMeasure : true
        },
        metrics() {
            const parts = this.report && this.report.parts ? this.report.parts : []
            // 后端已不返回臀部；前端再滤一层，避免脏缓存
            return parts.filter(item => item && item.present != null && item.key !== 'hip')
        },
        overallRows() {
            return this.report && this.report.overallAssessment ? this.report.overallAssessment : []
        },
        selectedMetric() {
            const fallback = {
                key: '',
                name: '--',
                present: null,
                change: null,
                view: ['front'],
                desc: '',
                high: null,
                low: null,
                last: null,
                regionTempDiff: null,
                diff: '--',
            }
            return this.metrics.find(item => item.key === this.selectedKey) || this.metrics[0] || fallback
        },
        visibleMetrics() {
            return this.metrics.filter(item => Array.isArray(item.view) && item.view.includes(this.view))
        },
        /** 后端真实 ROI（新表关键点推导）；无数据则不画框 */
        roiPoints() {
            const list = this.report && Array.isArray(this.report.roiPoints) ? this.report.roiPoints : []
            return list.filter(item =>
                item && item.key && item.key !== 'hip' && item.view && Array.isArray(item.points)
            )
        },
        currentThermalImg() {
            if (!this.report) return FALLBACK_IMG
            return this.view === 'back' ? (this.report.backImageUrl || FALLBACK_IMG) : (this.report.frontImageUrl || FALLBACK_IMG)
        },
        compareThermalImg() {
            if (!this.report || this.isFirstMeasure) return this.currentThermalImg
            return this.view === 'back'
                ? (this.report.compareBackImageUrl || FALLBACK_IMG)
                : (this.report.compareFrontImageUrl || FALLBACK_IMG)
        },
        currentTimeLabel() {
            if (!this.report || !this.report.measureTime) return '--'
            return historyTime(this.report.measureTime).label
        },
    },
    watch: {
        reportData: { immediate: true, handler() { this.selectedHistory = { label: '', scanId: '' }; this.loadReport() } },
        '$i18n.locale'() { this.loadReport(this.selectedHistory.scanId) },
    },
    mounted() {
        document.addEventListener('click', this.closePopups)
    },
    beforeDestroy() {
        document.removeEventListener('click', this.closePopups)
    },
    methods: {
        tr(text) { return domesticText(this.$i18n.locale, text) },
        loadReport(tcScanId) {
            const history = Array.isArray(this.reportData.compareRecords) ? this.reportData.compareRecords : [];
            const previous = history.find(item => item.scanId === tcScanId) || history[0];
            this.historyList = history.map(item => ({ ...historyTime(item.scanTime), scanId: item.scanId }));
            this.selectedHistory = previous ? { ...historyTime(previous.scanTime), scanId: previous.scanId } : { label: '', scanId: '' };
            this.report = infraredView(this.reportData, previous, this.$i18n.locale, this.imperial);
            this.loading = false;
            this.loadError = '';
            this.sheetOpen = false;
            if (!this.metrics.some(item => item.key === this.selectedKey) && this.metrics.length) this.selectedKey = this.metrics[0].key;
        },
        onSelectHistory(item) { this.historyOpen = false; this.loadReport(item.scanId) },
        closePopups() {
            this.historyOpen = false
            this.regionOpen = false
        },
        switchView(view) {
            this.view = view
            this.regionOpen = false
            const views = Array.isArray(this.selectedMetric.view) ? this.selectedMetric.view : []
            if (!views.includes(view) && this.visibleMetrics.length) {
                this.selectedKey = this.visibleMetrics[0].key
            }
        },
        selectRegion(key) {
            const metric = this.metrics.find(item => item.key === key)
            if (!metric) return
            const views = Array.isArray(metric.view) ? metric.view : []
            if (views.length && !views.includes(this.view)) {
                this.view = views[0]
            }
            this.selectedKey = key
            this.regionOpen = false
        },
        formatTemp(value, difference = false) { return temperature(value, this.imperial, difference) },
        formatChange(value) {
            if (value === null || value === undefined || value === '' || !Number.isFinite(Number(value))) return '—';
            return (value > 0 ? '↑' : value < 0 ? '↓' : '→') + this.formatTemp(Math.abs(value), true);
        },
        changeClass(value) {
            if (value == null || value === '' || Number.isNaN(Number(value))) return 'flat'
            const num = Number(value)
            if (Math.abs(num) < 0.05) return 'flat'
            return num > 0 ? 'up' : 'down'
        },
        changeLevel(value) {
            if (value == null || value === '' || Number.isNaN(Number(value))) return '--'
            const num = Number(value)
            if (num >= 0.6) return this.tr('明显升高')
            if (num <= -0.6) return this.tr('明显降低')
            if (Math.abs(num) >= 0.3) return this.tr('存在一定变化')
            return this.tr('变化较小')
        },
        openDetail(metric) {
            if (!metric) return;
            this.sheetTitle = metric.name + (/^(zh-|ja-)/.test(this.$i18n.locale) ? '' : ' ') + this.tr('热态数据');
            this.detailMetric = metric;
            this.sheetOpen = true;
        },
    },
}
</script>

<style lang="less" scoped>
.infrared-report {
    --cyan: #02d7e4;
    --muted: #8fa2c0;
    --green: #2dcc83;
    padding: 0 12px 30px;
    color: #f0f7ff;
}

.measure-mode-toggle {
    display: inline-flex;
    gap: 4px;
    margin: 0 4px 10px;
    padding: 4px;
    border: 1px solid rgba(2, 215, 228, 0.28);
    border-radius: 999px;
    background: rgba(2, 215, 228, 0.07);

    button {
        min-width: 86px;
        min-height: 30px;
        border: 0;
        border-radius: 999px;
        color: #9fb4d1;
        background: transparent;
        font-size: 14px;
        font-weight: 700;

        &.is-active {
            color: #071426;
            background: var(--cyan);
        }
    }
}

.section-head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin: 18px 4px 10px;

    h2 {
        margin: 0;
        font-size: 20px;
        font-weight: 400;
    }
}

.view-toggle {
    display: inline-flex;
    gap: 16px;

    button {
        position: relative;
        border: 0;
        padding: 4px 0 7px;
        color: var(--muted);
        background: transparent;
        font-size: 16px;

        &.is-active {
            color: var(--cyan);
            font-weight: 700;

            &::after {
                content: '';
                position: absolute;
                left: 2px;
                right: 2px;
                bottom: 0;
                height: 2px;
                border-radius: 999px;
                background: var(--cyan);
            }
        }
    }
}

.compare-card {
    position: relative;
    border: 1px solid rgba(80, 164, 224, 0.42);
    border-radius: 2px;
    background: linear-gradient(180deg, rgba(18, 44, 82, 0.88), rgba(10, 20, 42, 0.92));
    /* 历史下拉要能露出，不能裁切 */
    overflow: visible;

    &.is-first .compare-top {
        grid-template-columns: minmax(0, 1fr) 100px minmax(0, 1fr);

        > span:first-child {
            visibility: hidden;
        }
    }

    &.is-first .visual-compare {
        min-height: 390px;

        .thermal-panes {
            grid-template-columns: minmax(0, 1fr);
        }
    }

    &.is-first .thermal-pane.previous {
        display: none;
    }

    &.is-first .focus-panel {
        left: auto;
        right: 18px;
        top: 42%;
        transform: translateY(-50%);

        &::before,
        &::after {
            display: none;
        }
    }
}

.compare-top {
    display: grid;
    grid-template-columns: minmax(0, 1fr) 100px minmax(0, 1fr);
    align-items: center;
    gap: 4px;
    padding: 12px 8px 4px;
    color: var(--muted);
    font-size: 11px;
}

.compare-time {
    position: relative;
    min-width: 0;
    white-space: normal;
    overflow-wrap: anywhere;
    font-size: 11px;
    letter-spacing: -0.02em;

    &:last-child {
        text-align: right;
    }

    /* 只给触发按钮加箭头，不要落到历史选项上 */
    > button {
        border: 0;
        padding: 0;
        color: var(--cyan);
        background: transparent;
        font: inherit;
        font-weight: 700;
        white-space: normal;
        overflow-wrap: anywhere;

        &::after {
            content: '⌄';
            margin-left: 3px;
            font-size: 9px;
        }
    }
}

.history-panel {
    position: absolute;
    left: 0;
    top: calc(100% + 8px);
    z-index: 35;
    display: grid;
    gap: 8px;
    min-width: 168px;
    width: max-content;
    max-width: min(220px, calc(100vw - 48px));
    padding: 8px;
    border: 1px solid rgba(86, 151, 210, 0.32);
    border-radius: 12px;
    background: rgba(8, 25, 54, 0.98);
    box-shadow: 0 18px 36px rgba(0, 0, 0, 0.34);
}

.history-option {
    display: flex;
    justify-content: space-between;
    gap: 10px;
    border: 1px solid rgba(2, 215, 228, 0.26);
    border-radius: 8px;
    background: rgba(2, 215, 228, 0.08);
    color: #c9f7ff;
    padding: 8px;
    font-size: 11px;
    text-align: left;
    white-space: normal;
    overflow-wrap: anywhere;
}

.region-picker {
    position: relative;
    display: flex;
    justify-content: center;
    justify-self: center;
    width: fit-content;

    &.is-open .chevron {
        transform: rotate(180deg);
    }
}

.region-picker { max-width: 100%; }
.region-current {
    padding: 6px;
    box-sizing: border-box;
    max-width: 100%;
    overflow-wrap: anywhere;
    min-width: 80px;
    min-height: 30px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 4px;
    border: 1px solid rgba(2, 215, 228, 0.42);
    border-radius: 12px;
    color: var(--cyan);
    background: linear-gradient(180deg, rgba(2, 215, 228, 0.16), rgba(2, 215, 228, 0.06));
    font-size: 13px;
    font-weight: 700;

    &::before {
        content: '';
        width: 4px;
        height: 16px;
        border-radius: 99px;
        background: var(--cyan);
    }
}

.chevron {
    font-size: 11px;
    transition: transform 0.18s ease;
}

.region-menu {
    position: absolute;
    top: 100%;
    left: 0;
    right: 0;
    z-index: 30;
    width: 100%;
    min-width: 0;
    box-sizing: border-box;
    padding: 3px;
    border: 1px solid rgba(80, 164, 224, 0.38);
    border-radius: 12px;
    background: rgba(9, 28, 58, 0.98);
}

.region-option {
    width: 100%;
    min-height: 28px;
    border: 0;
    border-radius: 8px;
    color: #aebfda;
    background: transparent;
    font-size: 12px;
    padding: 0;
    white-space: normal;
    overflow-wrap: anywhere;

    &.is-active {
        color: var(--cyan);
        background: rgba(2, 215, 228, 0.12);
        font-weight: 700;
    }
}

.visual-compare {
    display: flex;
    align-items: stretch;
    gap: 6px;
    min-height: 360px;
    padding: 4px 4px 10px 6px;
}

.thermal-main {
    position: relative;
    flex: 1;
    min-width: 0;
}

.thermal-panes {
    display: grid;
    grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
    gap: 10px;
    align-items: stretch;
}

.ir-temp-bar {
    flex: 0 0 14px;
    display: flex;
    align-items: stretch;
    justify-content: center;
    padding: 12px 0 8px;
    box-sizing: border-box;
}

.ir-temp-gradient {
    width: 10px;
    border-radius: 999px;
    background: linear-gradient(
        180deg,
        #EF3E36 0%,
        #B02ACC 16.6667%,
        #FF9731 33.3333%,
        #EFDE33 50%,
        #7DDC46 66.6667%,
        #2A40DC 83.3333%,
        #080A12 100%
    );
}

.thermal-pane {
    min-width: 0;
    height: 348px;
    position: relative;

    &.previous {
        filter: saturate(0.72);
        opacity: 0.84;
    }
}

.heatmap-frame {
    position: relative;
    width: 100%;
    height: 100%;
    overflow: hidden;
    border-radius: 22px;
    background: radial-gradient(ellipse at 50% 45%, rgba(255, 255, 255, 0.08), transparent 58%);
}

.fullscreen-viewer .heatmap-img {
    width: 100%;
    max-width: 220px;
    height: 100%;
    object-fit: contain;
    pointer-events: none;
}

.focus-panel {
    position: absolute;
    left: 50%;
    top: 51%;
    z-index: 18;
    width: 74px;
    min-height: 60px;
    display: grid;
    align-content: center;
    gap: 3px;
    padding: 8px 4px;
    border: 1px solid rgba(2, 215, 228, 0.48);
    border-radius: 999px;
    background: linear-gradient(180deg, rgba(11, 38, 72, 0.82), rgba(6, 17, 36, 0.9));
    text-align: center;
    transform: translate(-50%, -50%);
    cursor: pointer;
    box-sizing: border-box;
    overflow: hidden;

    &::before,
    &::after {
        content: '';
        position: absolute;
        top: 50%;
        width: 18px;
        height: 1px;
        background: linear-gradient(90deg, transparent, rgba(2, 215, 228, 0.56));
    }

    &::before { right: 100%; transform: translateY(-50%); }
    &::after { left: 100%; transform: translateY(-50%) rotate(180deg); }
}

.focus-temp {
    display: grid;
    justify-items: center;
    gap: 3px;
    min-width: 0;
    max-width: 100%;

    b {
        display: block;
        font-size: 15px;
        line-height: 1;
        white-space: normal;
        overflow-wrap: anywhere;

        &.up { color: #ff7070; }
        &.down { color: #6aa1ff; }
        &.flat { color: var(--green); }
        &.mean { color: var(--cyan); }
    }

    span {
        display: block;
        max-width: 100%;
        color: #b9d6ef;
        font-size: 9px;
        line-height: 1.2;
        white-space: normal;
        overflow-wrap: anywhere;
        overflow: hidden;
    }
}

.module-title {
    position: relative;
    display: block;
    width: 100%;
    box-sizing: border-box;
    margin: 18px 0 8px;
    padding: 0 0 0 13px;
    color: #f3f8ff;
    font-size: 18px;
    line-height: 1.2;
    font-weight: 400;
    text-align: left;

    &::before {
        content: '';
        position: absolute;
        left: 0;
        top: 3px;
        bottom: 3px;
        width: 3px;
        border-radius: 999px;
        background: var(--cyan);
    }
}

.overall-card {
    width: 100%;
    box-sizing: border-box;
    border: 1px solid rgba(80, 164, 224, 0.36);
    border-radius: 10px;
    background: linear-gradient(180deg, rgba(18, 44, 82, 0.78), rgba(10, 22, 44, 0.88));
    overflow: hidden;
}

.overall-row {
    display: grid;
    grid-template-columns: 82px minmax(0, 1fr);
    border-bottom: 1px solid rgba(92, 142, 199, 0.18);

    &:last-child {
        border-bottom: 0;
    }

    strong {
        font-weight: 400;
        min-width: 0;
        overflow-wrap: anywhere;
        display: flex;
        align-items: center;
        margin: 0;
        padding: 12px;
        color: #f4f8ff;
        font-size: 14px;
        line-height: 1.45;
        background: rgba(42, 78, 124, 0.28);
        text-align: left;
    }

    span {
        min-width: 0;
        padding: 12px;
        color: #b8c9e2;
        font-size: 14px;
        line-height: 1.55;
        text-align: left;
        overflow-wrap: anywhere;
    }
}

.data-list {
    width: 100%;
    box-sizing: border-box;
    border: 1px solid rgba(80, 164, 224, 0.28);
    border-radius: 10px;
    background: linear-gradient(180deg, rgba(17, 40, 78, 0.62), rgba(9, 23, 49, 0.74));
    overflow: hidden;
}

.metric-card {
    width: 100%;
    position: relative;
    border: 0;
    border-bottom: 1px solid rgba(92, 142, 199, 0.18);
    background: transparent;
    padding: 0;
    text-align: left;
    color: inherit;

    &:last-child {
        border-bottom: 0;
    }

    &.is-active {
        background: linear-gradient(90deg, rgba(2, 215, 228, 0.12), rgba(14, 58, 86, 0.08), transparent);

        &::before {
            content: '';
            position: absolute;
            left: 0;
            top: 14px;
            bottom: 14px;
            width: 3px;
            border-radius: 999px;
            background: var(--cyan);
        }

        .metric-arrow {
            color: var(--cyan);
        }
    }
}

.metric-compare {
    display: grid;
    grid-template-columns: minmax(0, 1fr) fit-content(45%) 18px;
    align-items: center;
    min-height: 62px;
    gap: 10px;
    padding: 0 12px 0 14px;

    &.is-first .metric-change {
        color: var(--cyan);
        background: rgba(2, 215, 228, 0.12);
    }
}

.metric-label {
    display: flex;
    flex-wrap: wrap;
    min-width: 0;
    overflow-wrap: anywhere;
    align-items: baseline;
    gap: 8px;
    text-align: left;

    small {
        color: #6f86a9;
        font-size: 11px;
    }
}

.metric-name {
    color: #9fb4d1;
    font-size: 14px;
    font-weight: 650;
    text-align: left;
}

.metric-side {
    display: grid;
    min-width: 0;
    gap: 4px;
    text-align: left;
    justify-items: start;
}

.metric-center {
    min-width: 0;
    overflow-wrap: anywhere;
    text-align: end;
    display: grid;
    justify-items: end;
    gap: 4px;
}

.metric-level {
    color: #8298ba;
    font-size: 11px;
}

.metric-change {
    justify-self: end;
    max-width: 100%;
    box-sizing: border-box;
    padding: 0 8px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 64px;
    min-height: 28px;
    border-radius: 999px;
    background: rgba(255, 112, 112, 0.13);
    color: #ff7d7d;
    font-weight: 800;
    font-size: 14px;

    &.down {
        color: #6aa1ff;
        background: rgba(106, 161, 255, 0.16);
    }

    &.flat {
        color: var(--green);
        background: rgba(45, 204, 131, 0.16);
    }
}

.metric-arrow {
    color: rgba(154, 182, 224, 0.62);
    font-size: 20px;
}

.tip {
    margin-top: 16px;
    border: 1px solid rgba(117, 152, 206, 0.22);
    background: rgba(255, 255, 255, 0.04);
    color: #aab8d0;
    padding: 12px;
    line-height: 1.7;
    font-size: 13px;
    text-align:left;
}

.medical-note {
    margin-top: 10px;
    color: #7f90ad;
    font-size: 12px;
    line-height: 1.65;
    text-align:left;
}

.sheet-mask {
    position: fixed;
    inset: 0;
    z-index: 50;
    background: rgba(1, 8, 18, 0.62);
}

.roi-sheet {
    position: fixed;
    left: 50%;
    bottom: 0;
    z-index: 51;
    width: 100%;
    max-width: 420px;
    max-height: 88vh;
    overflow-y: auto;
    padding: 18px 18px 22px;
    border: 1px solid rgba(80, 160, 220, 0.42);
    background: linear-gradient(180deg, #172b50, #10172c);
    transform: translateX(-50%);
}

.sheet-head {
    display: flex;
    gap: 12px;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 12px;

    strong {
        flex: 1 1 0;
        min-width: 0;
        overflow-wrap: anywhere;
        text-align: start;
        color: var(--cyan);
        font-size: 18px;
    }
}

.sheet-close {
    flex: 0 0 32px;
    box-sizing: border-box;
    padding: 0;
    display: grid;
    place-items: center;
    line-height: 1;
    width: 32px;
    height: 32px;
    border: 1px solid rgba(117, 152, 206, 0.32);
    border-radius: 50%;
    color: #cfe5ff;
    background: rgba(255, 255, 255, 0.06);
}

.sheet-body {
    color: #d7e5f8;
    line-height: 1.7;
    font-size: 15px;
    text-align: left;

    /deep/ .detail-grid {
        display: grid;
        grid-template-columns: repeat(3, 1fr);
        gap: 8px;
        margin-bottom: 12px;

        &.detail-grid-4 {
            grid-template-columns: repeat(2, 1fr);
        }
    }

    /deep/ .detail-section {
        margin-top: 12px;
        text-align: left;

        &:first-child {
            margin-top: 0;
        }
    }

    /deep/ .detail-section-title {
        margin: 0 0 8px;
        color: #f2f8ff;
        font-size: 14px;
        font-weight: 800;
        text-align: left;
    }

    /deep/ .detail-eval {
        border: 1px solid rgba(80, 164, 224, 0.24);
        border-radius: 10px;
        overflow: hidden;
        background: rgba(255, 255, 255, 0.035);
    }

    /deep/ .detail-eval-row {
        display: grid;
        grid-template-columns: 82px minmax(0, 1fr);
        border-bottom: 1px solid rgba(117, 152, 206, 0.22);

        &:last-child {
            border-bottom: 0;
        }

        strong {
            display: flex;
            align-items: center;
            justify-content: center;
            margin: 0;
            padding: 10px;
            color: #eaf6ff;
            background: rgba(64, 145, 220, 0.16);
            font-size: 12px;
            line-height: 1.65;
            text-align: center;
        }

        span {
            min-width: 0;
            padding: 10px;
            color: #d7e5f8;
            font-size: 12px;
            line-height: 1.65;
            text-align: left;
            overflow-wrap: anywhere;
        }
    }

    /deep/ .detail-field {
        padding: 10px 8px;
        border: 1px solid rgba(80, 164, 224, 0.2);
        border-radius: 8px;
        background: rgba(255, 255, 255, 0.035);
        text-align: left;

        span {
            display: block;
            color: var(--muted);
            font-size: 12px;
            text-align: left;
        }

        b {
            display: block;
            margin-top: 4px;
            color: #eef7ff;
            font-size: 15px;
            text-align: left;
        }
    }

    /deep/ .detail-compare {
        display: grid;
        grid-template-columns: minmax(0, 1fr) auto minmax(0, 1fr);
        align-items: center;
        column-gap: 10px;
        margin-bottom: 12px;
        padding: 12px;
        border: 1px solid rgba(80, 164, 224, 0.24);
        border-radius: 8px;
        background: rgba(255, 255, 255, 0.035);
    }

    /deep/ .detail-compare-side {
        min-width: 0;

        b {
            display: block;
            color: var(--cyan);
            font-size: 22px;
            line-height: 1.1;
        }

        span {
            display: block;
            margin-top: 2px;
            color: var(--muted);
            font-size: 12px;
            line-height: 1.4;
        }

        &.is-prev {
            text-align: left;

            b,
            span {
                text-align: left;
            }
        }

        &.is-curr {
            text-align: right;

            b,
            span {
                text-align: right;
            }
        }
    }

    /deep/ .detail-change {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        justify-self: center;
        align-self: center;
        box-sizing: border-box;
        max-width: 100%;
        min-height: 28px;
        padding: 0 10px;
        border-radius: 999px;
        background: rgba(255, 112, 112, 0.16);
        color: #ff7070;
        font-weight: 800;
        white-space: normal;
        overflow-wrap: anywhere;
        line-height: 1;

        &.down {
            color: #6aa1ff;
            background: rgba(106, 161, 255, 0.16);
        }

        &.flat {
            color: var(--green);
            background: rgba(45, 204, 131, 0.16);
        }
    }

    /deep/ .detail-note {
        margin: 0;
        color: #aab8d0;
        font-size: 13px;
        line-height: 1.65;
        text-align: left;
        overflow-wrap: anywhere;
    }
}

.sheet-foot {
    margin: 12px 0 0;
    padding-top: 12px;
    border-top: 1px dashed rgba(117, 152, 206, 0.32);
    color: #8fa3c4;
    font-size: 13px;
    line-height: 1.6;
    text-align: left;
    overflow-wrap: anywhere;
}

.fullscreen-viewer {
    position: fixed;
    inset: 0;
    z-index: 80;
    display: grid;
    grid-template-rows: 58px 1fr;
    background: #050914;
}

.viewer-toolbar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 0 14px;
    color: #ddecff;
    background: rgba(15, 27, 52, 0.96);
    border-bottom: 1px solid rgba(80, 164, 224, 0.22);

    button {
        border: 1px solid rgba(80, 164, 224, 0.34);
        border-radius: 8px;
        background: rgba(255, 255, 255, 0.04);
        color: #ddecff;
        padding: 8px 10px;
        margin-left: 8px;
    }
}

.viewer-stage {
    display: grid;
    place-items: center;
    padding: 16px;
}

.heat-slider {
    --split: 50%;
    position: relative;
    width: 100%;
    max-width: 360px;
    height: 50vh;
    min-height: 330px;
    max-height: 430px;
    border: 1px solid rgba(80, 164, 224, 0.28);
    background: radial-gradient(circle at 50% 38%, rgba(17, 55, 96, 0.42), rgba(3, 13, 30, 0.08) 62%);
    overflow: hidden;

    &[data-view='front'] .back-only,
    &[data-view='back'] .front-only {
        display: none;
    }
}

.heat-layer {
    position: absolute;
    inset: 0;
    display: grid;
    place-items: center;

    &.last {
        z-index: 2;
        clip-path: inset(0 calc(100% - var(--split)) 0 0);
        filter: grayscale(0.72) saturate(0.72);
        opacity: 0.88;
    }

    .heatmap-img {
        width: 72%;
        max-width: 220px;
        height: 92%;
    }
}

.heat-label {
    position: absolute;
    top: 12px;
    z-index: 8;
    color: #a7b9d8;
    font-size: 13px;

    &.left { left: 12px; }
    &.right { right: 12px; }
}

.heat-divider {
    position: absolute;
    z-index: 7;
    top: 0;
    bottom: 0;
    left: var(--split);
    width: 2px;
    transform: translateX(-1px);
    background: rgba(2, 215, 228, 0.84);
}

.heat-handle {
    position: absolute;
    z-index: 9;
    left: var(--split);
    top: 50%;
    width: 38px;
    height: 38px;
    transform: translate(-50%, -50%);
    border: 1px solid rgba(2, 215, 228, 0.72);
    border-radius: 50%;
    background: rgba(12, 33, 66, 0.88);
    color: var(--cyan);
    display: grid;
    place-items: center;
    font-weight: 900;
    pointer-events: none;
}

.heat-range {
    position: absolute;
    z-index: 10;
    left: 16px;
    right: 16px;
    bottom: 12px;
    width: calc(100% - 32px);
    accent-color: var(--cyan);
}

.viewer-hint {
    margin-top: 12px;
    color: #8298ba;
    font-size: 12px;
    text-align: center;
}

.section-head { flex-wrap: wrap; gap: 8px; }
.section-head h2 { min-width: 0; overflow-wrap: anywhere; }
[dir="rtl"] .compare-time { text-align: start; }
[dir="rtl"] .history-panel { left: auto; right: 0; }
.history-option { direction: ltr; }
.compare-time > button, .compare-time.is-current { unicode-bidi: plaintext; }
@media (max-width: 360px) {
    .compare-time { white-space: normal; overflow-wrap: anywhere; }
    .compare-top { grid-template-columns: minmax(0, 1fr) 100px minmax(0, 1fr); }
}
</style>
